import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, BellOff, BellRing, CalendarRange, CheckCircle2, FlaskConical, History, Loader2, Mail, MessageSquareText,
  Pencil, Plus, Search, SendHorizonal, ShieldCheck, Smartphone, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { Progress } from "../ui/progress";
import { Skeleton } from "../ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { EMPTY_TERRITORY, TerritoryFilterSelects, splitIds, useTerritoryMasters } from "../TerritoryFilter";

/**
 * Phase 227 — Send Messages (Municipal Admin). Only the message types the Platform Super Admin set to MANUAL for this
 * municipality (plus the always-manual dues notices) can be sent here:
 *   message → who (whole municipality / zones / wards / mohallas — several at once — or selected properties + pasted PINs)
 *   → how (SMS / WhatsApp / e-mail) → date range or skip-days → Preview ("12 properties, 12 messages", the number /
 *   e-mail per property can be changed) → Confirm → background job with progress → recent sends.
 */
const TARGETS = [
  { key: "municipality", label: "Whole Municipality" },
  { key: "zone", label: "Whole Zone(s)" },
  { key: "ward", label: "Whole Ward(s)" },
  { key: "mohalla", label: "Whole Mohalla(s)" },
  { key: "properties", label: "Selected properties" },
];
const CHANNEL_META = { sms: { label: "SMS", icon: Smartphone }, whatsapp: { label: "WhatsApp", icon: MessageSquareText }, email: { label: "Email", icon: Mail } };
const SKIP_PRESETS = [0, 3, 7, 15, 30];
const STATUS_META = {
  send: { label: "Will send", variant: "secondary" },
  skip_already: { label: "Skipped · already sent", variant: "outline" },
  skip_recent: { label: "Skipped · notified recently", variant: "outline" },
  skip_no_contact: { label: "Skipped · no contact", variant: "outline" },
  skip_no_dues: { label: "Skipped · no dues", variant: "outline" },
  skip_no_text: { label: "Skipped · no text", variant: "outline" },
};
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const isoDay = (d) => d.toISOString().slice(0, 10);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const pluralUnit = (unit) => (unit === "property" ? "properties" : `${unit}s`);

function DetailCell({ basis, d }) {
  if (!d) return "—";
  if (basis === "dues") return <span>{inr(d.balance)} · {d.years || 0} yr{d.years === 1 ? "" : "s"}{d.last_notice_at ? <span className="text-muted-foreground"> · last {fmtDateTime(d.last_notice_at)}</span> : ""}</span>;
  if (basis === "receipt") return <span><span className="font-mono">{d.receipt_no || "—"}</span> · {inr(d.amount)} · {d.date}{d.mode ? ` · ${d.mode}` : ""}</span>;
  if (basis === "demand") return (
    <span>
      {inr(d.amount)} · {d.fy || "FY —"} · {d.date}
      {d.demands > 1 && <span className="text-muted-foreground" title={(d.heads || []).join(", ")} data-testid="sendmsg-demand-charges"> · {d.demands} charges combined</span>}
    </span>
  );
  if (basis === "visit") return <span>{d.date}{d.time ? ` ${d.time}` : ""} · {d.outcome}{d.by ? ` · ${d.by}` : ""}</span>;
  return "—";
}

/** mobile / e-mail picker for one row: known contacts in a dropdown + "Other…" free entry + "Don't use" */
function ContactPicker({ kind, contacts, value, onChange, testId }) {
  const options = kind === "mobile" ? contacts.mobiles : contacts.emails;
  const [custom, setCustom] = useState(false);
  const current = value === undefined ? (kind === "mobile" ? contacts.mobile : contacts.email) : value;
  const known = options.some((o) => o.value === current);
  useEffect(() => { if (current && !known) setCustom(true); }, [current, known]);
  if (custom) {
    return (
      <div className="flex items-center gap-1">
        <Input className="h-7 text-xs w-40" value={current || ""} placeholder={kind === "mobile" ? "10-digit mobile" : "name@example.com"}
          onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-input`} />
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setCustom(false); onChange(options[0]?.value || ""); }} title="Back to known contacts" data-testid={`${testId}-back`}><X className="h-3.5 w-3.5" /></Button>
      </div>
    );
  }
  return (
    <Select value={current || "__none"} onValueChange={(v) => { if (v === "__custom") { setCustom(true); onChange(""); } else onChange(v === "__none" ? "" : v); }}>
      <SelectTrigger className="h-7 text-xs w-44" data-testid={testId}>
        <SelectValue>{current ? (options.find((o) => o.value === current)?.label ? `${current} · ${options.find((o) => o.value === current).label}` : current) : "Don't use"}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.value} <span className="text-muted-foreground">· {o.label}</span></SelectItem>)}
        <SelectItem value="__custom"><Pencil className="h-3 w-3 inline mr-1" />Other {kind === "mobile" ? "number" : "address"}…</SelectItem>
        <SelectItem value="__none">Don't use {kind === "mobile" ? "a mobile" : "an e-mail"}</SelectItem>
      </SelectContent>
    </Select>
  );
}

export const SendMessagesPanel = ({ code, canEditDefault = true, testIdPrefix = "sendmsg", defaultEvent = null }) => {
  const T = (s) => `${testIdPrefix}-${s}`;
  const masters = useTerritoryMasters(code);
  const [settings, setSettings] = useState(null);
  const [event, setEvent] = useState(defaultEvent || "");
  const [targetType, setTargetType] = useState("ward");
  const [territory, setTerritory] = useState(EMPTY_TERRITORY);
  const [selected, setSelected] = useState([]);
  const [pinsText, setPinsText] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [channels, setChannels] = useState({ sms: true, whatsapp: false, email: false });
  const [dateFrom, setDateFrom] = useState(isoDay(new Date(Date.now() - 29 * 86400000)));
  const [dateTo, setDateTo] = useState(isoDay(new Date()));
  const [skipDays, setSkipDays] = useState(7);
  const [customDays, setCustomDays] = useState("");
  const [savingDefault, setSavingDefault] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [job, setJob] = useState(null);
  const [recent, setRecent] = useState([]);
  const pollRef = useRef(null);
  const overrideTimer = useRef(null);

  const loadSettings = useCallback(() => {
    api.get(`/t/${code}/messages/send/settings`).then((r) => {
      setSettings(r.data);
      setSkipDays(r.data.notice_skip_days ?? 7);
      const evs = r.data.events || [];
      setEvent((cur) => (cur && evs.some((e) => e.event === cur)) ? cur : (evs[0]?.event || ""));
      const ch = r.data.channels || {};
      setChannels((c) => ({ sms: ch.sms?.on !== false && c.sms, whatsapp: c.whatsapp && ch.whatsapp?.on !== false, email: c.email && ch.email?.on !== false }));
    }).catch((e) => toast.error(errMsg(e)));
  }, [code]);
  const loadRecent = useCallback(() => {
    api.get(`/t/${code}/messages/send/jobs`, { params: { limit: 5 } }).then((r) => setRecent(r.data.items || [])).catch(() => {});
  }, [code]);
  useEffect(() => { loadSettings(); loadRecent(); }, [loadSettings, loadRecent]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); if (overrideTimer.current) clearTimeout(overrideTimer.current); }, []);

  // property search (debounced)
  useEffect(() => {
    if (targetType !== "properties" || query.trim().length < 2) { setResults([]); return undefined; }
    const h = setTimeout(() => {
      setSearching(true);
      api.get(`/t/${code}/messages/send/properties`, { params: { q: query.trim(), limit: 20 } })
        .then((r) => setResults(r.data.items || [])).catch(() => setResults([])).finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(h);
  }, [query, targetType, code]);

  const eventMeta = settings?.events?.find((e) => e.event === event);
  const basis = eventMeta?.basis;
  const needsRange = basis && basis !== "dues";
  const pins = useMemo(() => pinsText.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean), [pinsText]);
  const target = useMemo(() => {
    if (targetType === "municipality") return { type: "municipality" };
    if (targetType === "zone") return { type: "zone", ids: splitIds(territory.zoneId) };
    if (targetType === "ward") return { type: "ward", ids: splitIds(territory.wardId) };
    if (targetType === "mohalla") return { type: "mohalla", ids: splitIds(territory.mohallaId) };
    return { type: "properties", ids: selected.map((p) => p.id), pins };
  }, [targetType, territory, selected, pins]);
  const targetReady = targetType === "municipality" || (targetType === "properties" ? (selected.length > 0 || pins.length > 0) : (target.ids || []).length > 0);
  const chosenChannels = Object.keys(channels).filter((c) => channels[c]);
  const levels = targetType === "zone" ? ["zone"] : targetType === "ward" ? ["zone", "ward"] : ["zone", "ward", "mohalla"];

  // any change to the selection invalidates the preview (contact edits re-run it below)
  useEffect(() => { setPreview(null); setOverrides({}); }, [event, targetType, territory, selected, pinsText, chosenChannels.join("|"), dateFrom, dateTo, skipDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const body = (ov = overrides) => ({ event, target, channels: chosenChannels, date_from: needsRange ? dateFrom : undefined, date_to: needsRange ? dateTo : undefined,
    skip_days: basis === "dues" ? skipDays : undefined, overrides: Object.keys(ov).length ? ov : undefined });

  const runPreview = async (ov = overrides, silent = false) => {
    if (!event) return toast.error("Choose a message type");
    if (!targetReady) return toast.error(targetType === "properties" ? "Pick at least one property or paste PINs" : targetType === "municipality" ? "" : `Choose at least one ${targetType}`);
    if (!chosenChannels.length) return toast.error("Choose how to send: SMS, WhatsApp and/or e-mail");
    setPreviewBusy(true); setJob(null);
    try {
      const r = await api.post(`/t/${code}/messages/send/preview`, body(ov));
      setPreview(r.data);
      if (!silent && r.data.totals.messages === 0) toast.warning("Nothing to send", { description: "Every selected item is skipped — see the reasons in the list." });
    } catch (e) { toast.error(errMsg(e)); } finally { setPreviewBusy(false); }
  };

  const setContact = (propertyId, kind, value) => {
    const next = { ...overrides, [propertyId]: { ...(overrides[propertyId] || {}), [kind]: value } };
    setOverrides(next);
    if (overrideTimer.current) clearTimeout(overrideTimer.current);
    overrideTimer.current = setTimeout(() => runPreview(next, true), 600);
  };

  const pollJob = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/t/${code}/messages/send/jobs/${id}`);
        setJob(r.data);
        if (r.data.status !== "running") {
          clearInterval(pollRef.current); pollRef.current = null;
          loadRecent();
          const d = r.data;
          if (d.status === "done") toast.success(`${d.event_label} sent`, { description: `${d.sent} sent · ${d.failed} failed${d.suppressed ? ` · ${d.suppressed} held back (test list)` : ""}${d.simulated ? ` · ${d.simulated} simulated` : ""}${d.skipped ? ` · ${d.skipped} skipped` : ""}` });
          else toast.error("Send stopped with an error", { description: d.error });
        }
      } catch { /* keep polling */ }
    }, 1500);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const r = await api.post(`/t/${code}/messages/send`, { ...body(), confirm: true, expected_messages: preview.totals.messages });
      setConfirmOpen(false); setJob(r.data); setPreview(null); setOverrides({});
      pollJob(r.data.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  const saveDefault = async () => {
    setSavingDefault(true);
    try {
      await api.put(`/t/${code}/notification-prefs`, { notice_skip_days: skipDays });
      toast.success(`Municipality default saved: ${skipDays === 0 ? "no skipping" : `skip within ${skipDays} days`}`);
      loadSettings();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingDefault(false); }
  };

  const addProperty = (p) => { if (!selected.some((x) => x.id === p.id)) setSelected((s) => [...s, p]); setQuery(""); setResults([]); };
  const presetValue = SKIP_PRESETS.includes(skipDays) && customDays === "" ? String(skipDays) : "custom";

  if (!settings) return <Skeleton className="h-48 w-full" data-testid={T("loading")} />;
  const totals = preview?.totals;
  const channelsOn = settings.channels || {};

  return (
    <div className="space-y-4" data-testid={T("panel")}>
      {!settings.citizen_enabled && (
        <Card className="border-destructive/50" data-testid={T("citizen-off")}>
          <CardContent className="p-4 flex items-start gap-3">
            <BellOff className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Citizen messages are switched off for this municipality</p>
              <p className="text-xs text-muted-foreground mt-0.5">The Platform Super Admin has switched Citizen messages Off (Tenant Configuration → Messages). Nothing can be sent — automatic and manual alike — until they are On again; every attempt is listed as Skipped in Notification History.</p>
            </div>
          </CardContent>
        </Card>
      )}
      {settings.citizen_enabled && settings.events.length === 0 && (
        <Card data-testid={T("nothing-manual")}>
          <CardContent className="p-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Every message type is automatic for this municipality</p>
              <p className="text-xs text-muted-foreground mt-0.5">There is nothing to send by hand. The Platform Super Admin decides which types are Manual (Tenant Configuration → Messages).</p>
            </div>
          </CardContent>
        </Card>
      )}

      {settings.events.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2"><SendHorizonal className="h-4 w-4" /> Send messages</CardTitle>
            <p className="text-xs text-muted-foreground">Only the message types set to <span className="font-medium text-foreground">Manual</span> for this municipality appear here. Choose the message, who gets it and how, preview the list, then confirm.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* ---- what ---- */}
              <div className="space-y-1.5">
                <Label className="text-xs">Message</Label>
                <Select value={event} onValueChange={(v) => v && setEvent(v)}>
                  <SelectTrigger data-testid={T("event-select")}><SelectValue placeholder="Choose a message">{eventMeta?.label}</SelectValue></SelectTrigger>
                  <SelectContent>
                    {settings.events.map((e) => <SelectItem key={e.event} value={e.event} data-testid={T(`event-option-${e.event}`)}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {eventMeta?.hint && <p className="text-[11px] text-muted-foreground" data-testid={T("event-hint")}>{eventMeta.hint}</p>}
                {eventMeta && (eventMeta.dlt_approved ? (
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={T("event-dlt")}><ShieldCheck className="h-3 w-3" /> SMS: DLT {eventMeta.dlt_template_id}{eventMeta.dlt_header ? ` · sender ${eventMeta.dlt_header}` : ""}</p>
                ) : eventMeta.sms_live_blocked ? (
                  <p className="text-[11px] text-destructive flex items-start gap-1" data-testid={T("event-live-blocked")}><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> No DLT-approved SMS text yet — live SMS is blocked for this type (WhatsApp / e-mail can still be sent).</p>
                ) : (
                  <p className="text-[11px] text-amber-700 dark:text-amber-400 flex items-start gap-1" data-testid={T("event-no-dlt")}><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> No DLT-approved SMS text yet — allowed in simulation mode only.</p>
                ))}
              </div>
              {/* ---- who ---- */}
              <div className="space-y-1.5">
                <Label className="text-xs">Who gets it</Label>
                <Select value={targetType} onValueChange={(v) => v && setTargetType(v)}>
                  <SelectTrigger data-testid={T("target-type-select")}><SelectValue>{TARGETS.find((t) => t.key === targetType)?.label}</SelectValue></SelectTrigger>
                  <SelectContent>{TARGETS.map((t) => <SelectItem key={t.key} value={t.key} data-testid={T(`target-type-${t.key}`)}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  {targetType === "municipality" ? "Every property of the municipality." : targetType === "properties" ? `Search and pick properties, or paste up to ${settings.max_pins} PINs.` : `Pick one or more ${targetType}s — every property inside them.`}
                </p>
              </div>
              {/* ---- how ---- */}
              <div className="space-y-1.5">
                <Label className="text-xs">How to send</Label>
                <div className="flex flex-col gap-1.5 pt-1" data-testid={T("channels")}>
                  {Object.entries(CHANNEL_META).map(([ch, m]) => {
                    const on = channelsOn[ch]?.on !== false;
                    const noText = eventMeta && eventMeta.texts && !eventMeta.texts[ch];
                    return (
                      <label key={ch} className={`flex items-center gap-2 text-sm ${on ? "cursor-pointer" : "opacity-60"}`}>
                        <Checkbox checked={!!channels[ch]} disabled={!on} onCheckedChange={(v) => setChannels((c) => ({ ...c, [ch]: !!v }))} data-testid={T(`channel-${ch}`)} />
                        <m.icon className="h-3.5 w-3.5 text-muted-foreground" /> {m.label}
                        {!on && <span className="text-[11px] text-muted-foreground">— {channelsOn[ch]?.reason}</span>}
                        {on && noText && <span className="text-[11px] text-amber-700 dark:text-amber-400">— no template text for this type</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ---- target pickers ---- */}
            {targetType !== "municipality" && targetType !== "properties" && (
              <TerritoryFilterSelects masters={masters} value={territory} onChange={setTerritory} levels={levels} compact testIdPrefix={T("territory")} />
            )}
            {targetType === "properties" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1"><Search className="h-3 w-3" /> Find properties (PIN, owner, mobile or address)</Label>
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Type at least 2 characters…" data-testid={T("property-search")} />
                  {(results.length > 0 || searching) && (
                    <div className="border rounded-md max-h-56 overflow-auto divide-y" data-testid={T("property-results")}>
                      {searching && <p className="text-xs text-muted-foreground p-2 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Searching…</p>}
                      {results.map((p) => (
                        <button key={p.id} type="button" onClick={() => addProperty(p)} disabled={selected.some((x) => x.id === p.id)}
                          className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-accent disabled:opacity-50 flex items-center justify-between gap-2" data-testid={T(`property-result-${p.pin}`)}>
                          <span><span className="font-mono">{p.pin}</span> · {p.owner_name || "—"} <span className="text-muted-foreground">· {[p.ward, p.mohalla].filter(Boolean).join(" / ")}</span></span>
                          <span className="flex items-center gap-1 text-muted-foreground">{p.has_mobile ? <Smartphone className="h-3 w-3" /> : null}{p.has_email ? <Mail className="h-3 w-3" /> : null}<Plus className="h-3.5 w-3.5" /></span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5" data-testid={T("selected-properties")}>
                    {selected.map((p) => (
                      <Badge key={p.id} variant="secondary" className="gap-1 font-mono text-[11px]" data-testid={T(`selected-${p.pin}`)}>
                        {p.pin}
                        <button type="button" onClick={() => setSelected((s) => s.filter((x) => x.id !== p.id))} aria-label={`Remove ${p.pin}`} data-testid={T(`remove-${p.pin}`)}><X className="h-3 w-3" /></button>
                      </Badge>
                    ))}
                    {selected.length === 0 && <span className="text-[11px] text-muted-foreground">No property picked yet.</span>}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">…or paste property PINs</Label>
                  <Textarea rows={4} value={pinsText} onChange={(e) => setPinsText(e.target.value)} placeholder={"BMC100001\nBMC100002, BMC100003"} className="font-mono text-xs" data-testid={T("pins-textarea")} />
                  <p className="text-[11px] text-muted-foreground">{plural(pins.length, "PIN", "PINs")} pasted · {plural(selected.length, "property", "properties")} picked</p>
                </div>
              </div>
            )}

            {/* ---- range / skip rule ---- */}
            {eventMeta && (needsRange ? (
              <div className="flex flex-wrap items-end gap-3" data-testid={T("date-range")}>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1"><CalendarRange className="h-3 w-3" /> {eventMeta.range_label || "Between"}</Label>
                  <div className="flex items-center gap-2">
                    <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid={T("date-from")} />
                    <span className="text-xs text-muted-foreground">and</span>
                    <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid={T("date-to")} />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground pb-2" data-testid={T("unit-hint")}>
                  One message per {eventMeta.unit} in this range; {pluralUnit(eventMeta.unit)} that already got their message are skipped.
                  {eventMeta.unit_hint ? ` ${eventMeta.unit_hint}` : ""}
                </p>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Skip properties notified in the last…</Label>
                  <div className="flex items-center gap-2">
                    <Select value={presetValue} onValueChange={(v) => { if (v === "custom") { setCustomDays(String(skipDays)); } else { setCustomDays(""); setSkipDays(Number(v)); } }}>
                      <SelectTrigger className="w-40 h-9" data-testid={T("skip-days-select")}><SelectValue>{presetValue === "custom" ? "Custom…" : skipDays === 0 ? "Don't skip (0)" : `${skipDays} days`}</SelectValue></SelectTrigger>
                      <SelectContent>
                        {SKIP_PRESETS.map((d) => <SelectItem key={d} value={String(d)} data-testid={T(`skip-days-${d}`)}>{d === 0 ? "Don't skip (0)" : `${d} days`}</SelectItem>)}
                        <SelectItem value="custom" data-testid={T("skip-days-custom")}>Custom…</SelectItem>
                      </SelectContent>
                    </Select>
                    {presetValue === "custom" && (
                      <Input type="number" min={0} max={settings.skip_days_max} className="w-24 h-9" value={customDays}
                        onChange={(e) => { setCustomDays(e.target.value); const n = Number(e.target.value); if (Number.isFinite(n)) setSkipDays(Math.max(0, Math.min(settings.skip_days_max, Math.round(n)))); }}
                        data-testid={T("skip-days-input")} />
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground pb-2" data-testid={T("skip-days-rule")}>
                  Rule for this send: <span className="font-medium text-foreground">{skipDays === 0 ? "no skipping" : `skip within ${skipDays} day${skipDays === 1 ? "" : "s"}`}</span>
                  {" · "}default: {settings.notice_skip_days === 0 ? "no skipping" : `${settings.notice_skip_days} days`}
                  {canEditDefault && skipDays !== settings.notice_skip_days && (
                    <Button variant="link" size="sm" className="h-auto p-0 ml-1.5 text-[11px]" onClick={saveDefault} disabled={savingDefault} data-testid={T("save-default-days")}>{savingDefault ? "Saving…" : "Save as default"}</Button>
                  )}
                </p>
              </div>
            ))}

            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => runPreview()} disabled={previewBusy || !targetReady || !event || !chosenChannels.length || !settings.citizen_enabled} data-testid={T("preview-button")}>
                {previewBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Users className="h-4 w-4 mr-1.5" />} Preview recipients
              </Button>
              <Button onClick={() => setConfirmOpen(true)} disabled={!preview || !totals || totals.messages === 0 || !settings.citizen_enabled || (channels.sms && eventMeta?.sms_live_blocked)}
                title={channels.sms && eventMeta?.sms_live_blocked ? "No DLT-approved SMS text — untick SMS to send the other channels" : undefined} data-testid={T("send-button")}>
                <SendHorizonal className="h-4 w-4 mr-1.5" /> Send {totals ? `${plural(totals.messages, "message", "messages")} to ${plural(totals.properties, "property", "properties")}` : ""}
              </Button>
              {settings.simulation_mode && <Badge variant="outline" className="gap-1 text-[10px]" data-testid={T("sim-badge")}><FlaskConical className="h-3 w-3" /> Simulation mode — nothing really goes out</Badge>}
              {!settings.simulation_mode && settings.live_allow_list_count > 0 && <Badge variant="outline" className="gap-1 text-[10px]" data-testid={T("allowlist-badge")}><ShieldCheck className="h-3 w-3" /> Test allow-list active ({settings.live_allow_list_count}) — other numbers are held back</Badge>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---------- preview ---------- */}
      {preview && (
        <Card className="shadow-sm" data-testid={T("preview-card")}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base font-heading">Recipients — {preview.target.label}</CardTitle>
              <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                <Badge data-testid={T("summary")} className="text-xs">{preview.summary}</Badge>
                {Object.entries(totals.by_channel).filter(([, n]) => n > 0).map(([c, n]) => <Badge key={c} variant="secondary" data-testid={T(`by-channel-${c}`)}>{n} {CHANNEL_META[c].label}</Badge>)}
                {totals.skipped.already_sent > 0 && <Badge variant="outline" data-testid={T("skip-already")}>{totals.skipped.already_sent} already sent</Badge>}
                {totals.skipped.recent > 0 && <Badge variant="outline" data-testid={T("skip-recent")}>{totals.skipped.recent} notified within {preview.skip_days} days</Badge>}
                {totals.skipped.no_contact > 0 && <Badge variant="outline" data-testid={T("skip-no-contact")}>{totals.skipped.no_contact} no contact</Badge>}
                {totals.skipped.no_dues > 0 && <Badge variant="outline" data-testid={T("skip-no-dues")}>{totals.skipped.no_dues} no dues</Badge>}
                {totals.skipped.no_text > 0 && <Badge variant="outline" data-testid={T("skip-no-text")}>{totals.skipped.no_text} no text</Badge>}
                <Badge variant="outline" data-testid={T("total-rows")}>{plural(totals.rows, preview.unit, pluralUnit(preview.unit))} · {preview.target.properties_selected} selected</Badge>
              </div>
            </div>
            {preview.channels_dropped?.length > 0 && <p className="text-xs text-amber-700 dark:text-amber-400">Not sent via {preview.channels_dropped.map((c) => `${CHANNEL_META[c.channel].label} (${c.reason})`).join(", ")}.</p>}
            {preview.channels_without_text?.length > 0 && <p className="text-xs text-amber-700 dark:text-amber-400" data-testid={T("no-text-note")}>No template text for {preview.channels_without_text.map((c) => CHANNEL_META[c].label).join(", ")} — those channels are left out.</p>}
            {preview.unknown_pins?.length > 0 && <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1" data-testid={T("unknown-pins")}><AlertTriangle className="h-3.5 w-3.5" /> Not found: {preview.unknown_pins.join(", ")}</p>}
            <p className="text-xs text-muted-foreground">Change the mobile number or e-mail address used for a property in the list — counts refresh automatically.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {preview.message_sample && (
              <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-2" data-testid={T("message-sample")}>
                <p className="font-medium flex items-center gap-1.5 flex-wrap">Exact message for {preview.message_sample.pin}
                  {preview.message_sample.dlt_template_id && <Badge variant="secondary" className="text-[10px]">DLT {preview.message_sample.dlt_template_id}</Badge>}
                  {preview.message_sample.header && <Badge variant="outline" className="text-[10px]">Sender {preview.message_sample.header}</Badge>}
                </p>
                <Tabs defaultValue={preview.channels[0]}>
                  <TabsList className="h-8">
                    {preview.channels.map((c) => <TabsTrigger key={c} value={c} className="text-xs h-7" data-testid={T(`sample-tab-${c}`)}>{CHANNEL_META[c].label}</TabsTrigger>)}
                  </TabsList>
                  {preview.channels.map((c) => (
                    <TabsContent key={c} value={c}>
                      {c === "email" && preview.message_sample.email_subject && <p className="font-medium mb-1">Subject: {preview.message_sample.email_subject}</p>}
                      <pre className="whitespace-pre-wrap font-sans text-xs border rounded p-2 bg-background" data-testid={T(`sample-text-${c}`)}>{c === "email" ? preview.message_sample.email_body : preview.message_sample[c] || "(no text)"}</pre>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            )}
            <div className="max-h-[460px] overflow-auto border rounded-md">
              <Table data-testid={T("preview-table")}>
                <TableHeader>
                  <TableRow>
                    <TableHead>PIN</TableHead><TableHead>Owner</TableHead>
                    <TableHead className="hidden lg:table-cell">Ward / Mohalla</TableHead>
                    <TableHead>{preview.basis === "dues" ? "Dues" : preview.basis === "receipt" ? "Receipt" : preview.basis === "demand" ? "Demand" : "Visit"}</TableHead>
                    {preview.channels.some((c) => c !== "email") && <TableHead>Mobile</TableHead>}
                    {preview.channels.includes("email") && <TableHead>Email</TableHead>}
                    <TableHead>Via</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r) => (
                    <TableRow key={r.key} className={r.status !== "send" ? "opacity-60" : ""} data-testid={T(`row-${r.key}`)}>
                      <TableCell className="font-mono text-xs">{r.pin}</TableCell>
                      <TableCell className="text-xs">{r.owner_name || "—"}</TableCell>
                      <TableCell className="text-xs hidden lg:table-cell">{[r.ward, r.mohalla].filter(Boolean).join(" / ") || "—"}</TableCell>
                      <TableCell className="text-xs"><DetailCell basis={preview.basis} d={r.detail} /></TableCell>
                      {preview.channels.some((c) => c !== "email") && (
                        <TableCell>
                          <ContactPicker kind="mobile" contacts={r.contacts} value={overrides[r.property_id]?.mobile} onChange={(v) => setContact(r.property_id, "mobile", v)} testId={T(`mobile-${r.pin}`)} />
                        </TableCell>
                      )}
                      {preview.channels.includes("email") && (
                        <TableCell>
                          <ContactPicker kind="email" contacts={r.contacts} value={overrides[r.property_id]?.email} onChange={(v) => setContact(r.property_id, "email", v)} testId={T(`email-${r.pin}`)} />
                        </TableCell>
                      )}
                      <TableCell className="text-xs">{r.channels.length ? r.channels.map((c) => CHANNEL_META[c].label).join(" + ") : "—"}</TableCell>
                      <TableCell><Badge variant={STATUS_META[r.status]?.variant || "outline"} className="text-[10px]" title={r.reason} data-testid={T(`status-${r.key}`)}>{STATUS_META[r.status]?.label || r.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {preview.rows_truncated && <p className="text-[11px] text-muted-foreground">Showing the first {preview.rows.length} rows of {totals.rows}; the totals above cover everything.</p>}
          </CardContent>
        </Card>
      )}

      {/* ---------- job ---------- */}
      {job && (
        <Card className="shadow-sm" data-testid={T("job-card")}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm font-medium flex items-center gap-2">
                {job.status === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : job.status === "done" ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" /> : <AlertTriangle className="h-4 w-4 text-destructive" />}
                {job.event_label} → {job.target?.label} · via {(job.channels || []).map((c) => CHANNEL_META[c]?.label || c).join(", ")}
              </p>
              <Badge variant={job.status === "error" ? "destructive" : "secondary"} data-testid={T("job-status")}>{job.status === "running" ? `Sending ${job.done}/${job.total}` : job.status}</Badge>
            </div>
            <Progress value={job.total ? Math.round((job.done / job.total) * 100) : 0} className="h-2" />
            <p className="text-xs text-muted-foreground" data-testid={T("job-summary")}>
              {job.sent} sent · {job.failed} failed{job.suppressed ? ` · ${job.suppressed} held back (test allow-list)` : ""}{job.simulated ? ` · ${job.simulated} simulated` : ""}{job.skipped ? ` · ${job.skipped} skipped` : ""}{job.no_message ? ` · ${job.no_message} not sent` : ""}
              {job.status !== "running" && " — every message is in Notification History; a summary was added to the admins' bell."}
            </p>
            {job.error && <p className="text-xs text-destructive">{job.error}</p>}
          </CardContent>
        </Card>
      )}

      {/* ---------- recent ---------- */}
      {recent.length > 0 && (
        <Card className="shadow-sm" data-testid={T("recent-card")}>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><History className="h-4 w-4" /> Recent sends</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Message</TableHead><TableHead>To</TableHead><TableHead className="hidden md:table-cell">Via</TableHead><TableHead>By</TableHead><TableHead className="text-right">Sent / Failed</TableHead></TableRow></TableHeader>
              <TableBody>
                {recent.map((j) => (
                  <TableRow key={j.id} data-testid={T(`recent-${j.id}`)}>
                    <TableCell className="text-xs">{fmtDateTime(j.started_at)}</TableCell>
                    <TableCell className="text-xs">{j.event_label}</TableCell>
                    <TableCell className="text-xs">{j.target?.label}</TableCell>
                    <TableCell className="text-xs hidden md:table-cell">{(j.channels || []).map((c) => CHANNEL_META[c]?.label || c).join(", ")}</TableCell>
                    <TableCell className="text-xs">{j.by?.name || "—"}</TableCell>
                    <TableCell className="text-xs text-right">{j.sent} / {j.failed}{j.status === "running" ? " (running)" : ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ---------- confirm ---------- */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid={T("confirm-dialog")}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BellRing className="h-4 w-4" /> Send {eventMeta?.label}?</DialogTitle>
            <DialogDescription>Every property marked "Will send" gets the message on the chosen channels. Messages cannot be recalled.</DialogDescription>
          </DialogHeader>
          {preview && totals && (
            <div className="text-sm space-y-1.5" data-testid={T("confirm-summary")}>
              <p className="text-base font-semibold" data-testid={T("confirm-counts")}>{preview.summary}</p>
              <p><span className="text-muted-foreground">To:</span> {preview.target.label}</p>
              <p><span className="text-muted-foreground">Via:</span> {preview.channels.map((c) => `${CHANNEL_META[c].label} (${totals.by_channel[c]})`).join(", ")}</p>
              {needsRange && <p><span className="text-muted-foreground">{eventMeta?.range_label}:</span> {preview.date_from} and {preview.date_to}</p>}
              {preview.basis === "dues" && <p><span className="text-muted-foreground">Skip rule:</span> {preview.skip_days === 0 ? "none" : `notified within ${preview.skip_days} days are skipped`} · total dues {inr(totals.amount)}</p>}
              <p className="text-xs text-muted-foreground">{settings.simulation_mode ? "SIMULATION — nothing really goes out." : settings.live_allow_list_count > 0 ? `Test allow-list active (${settings.live_allow_list_count} numbers) — other numbers are held back.` : "Live sending — gateway charges apply."}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending} data-testid={T("confirm-cancel")}>Cancel</Button>
            <Button onClick={confirmSend} disabled={sending} data-testid={T("confirm-send")}>
              {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <SendHorizonal className="h-4 w-4 mr-1.5" />} Yes, send {totals?.messages ?? 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SendMessagesPanel;
